import { getGoogleDrivePickerConfig, startGoogleDriveOnePick } from "@/lib/api/google-drive";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const GOOGLE_DOC_MIME = "application/vnd.google-apps.document";
const GOOGLE_SHEET_MIME = "application/vnd.google-apps.spreadsheet";
const GOOGLE_SLIDES_MIME = "application/vnd.google-apps.presentation";

export const CONTRACT_PICKER_MIME_TYPES = [
  "application/pdf",
  DOCX_MIME,
  GOOGLE_DOC_MIME,
] as const;

export const KM_PICKER_MIME_TYPES = [
  "application/pdf",
  DOCX_MIME,
  GOOGLE_DOC_MIME,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  GOOGLE_SLIDES_MIME,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  GOOGLE_SHEET_MIME,
  "text/html",
  "text/markdown",
  "text/csv",
] as const;

export interface PickerSelectedFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number | null;
  resourceKey?: string | null;
  accessToken?: string;
}

interface GooglePickerDocument {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
  resourceKey?: string;
  driveError?: string;
  driveSuccess?: boolean;
}

interface GooglePickerResponse {
  action: string;
  docs?: GooglePickerDocument[];
}

interface GooglePickerBuilder {
  setOAuthToken(token: string): GooglePickerBuilder;
  setDeveloperKey(key: string): GooglePickerBuilder;
  setAppId(appId: string): GooglePickerBuilder;
  setOrigin(origin: string): GooglePickerBuilder;
  setSize(width: number, height: number): GooglePickerBuilder;
  addView(view: unknown): GooglePickerBuilder;
  enableFeature(feature: string): GooglePickerBuilder;
  setCallback(callback: (data: GooglePickerResponse) => void): GooglePickerBuilder;
  build(): { setVisible(visible: boolean): void };
}

interface GoogleDocsView {
  setMimeTypes(mimeTypes: string): GoogleDocsView;
  setIncludeFolders(include: boolean): GoogleDocsView;
  setSelectFolderEnabled(enabled: boolean): GoogleDocsView;
}

interface GooglePickerNamespace {
  PickerBuilder: new () => GooglePickerBuilder;
  ViewId: { DOCS: string };
  DocsView: new (viewId?: string) => GoogleDocsView;
  Feature: { MULTISELECT_ENABLED: string; SUPPORT_DRIVES: string };
  Action: { PICKED: string; CANCEL: string };
}

declare global {
  interface Window {
    gapi?: {
      load: (api: string, callback: () => void) => void;
      client?: unknown;
    };
    google?: {
      picker?: GooglePickerNamespace;
    };
  }
}

let pickerScriptPromise: Promise<void> | null = null;
let activePickerSession: Promise<PickerSelectedFile[]> | null = null;
let activePickerInstance: { setVisible(visible: boolean): void } | null = null;
let pickerControlsCleanup: (() => void) | null = null;

function removePickerControls(): void {
  pickerControlsCleanup?.();
  pickerControlsCleanup = null;
}

function getActivePickerDialog(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".picker-dialog:last-of-type");
}

function getActivePickerBackdrop(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".picker-dialog-bg:last-of-type");
}

function attachPickerControls(onClose: () => void): void {
  removePickerControls();

  let button: HTMLButtonElement | null = null;
  let outsideClickAttached = false;
  let observer: MutationObserver | null = null;
  let rafId = 0;
  let attempts = 0;

  const handleClose = (event?: Event) => {
    event?.preventDefault();
    event?.stopPropagation();
    window.requestAnimationFrame(() => onClose());
  };

  const repositionButton = () => {
    if (!button) return;
    const dialog = getActivePickerDialog();
    if (!dialog) {
      button.hidden = true;
      return;
    }

    const rect = dialog.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      button.hidden = true;
      return;
    }

    button.hidden = false;
    button.style.top = `${Math.max(rect.top + 8, 8)}px`;
    button.style.left = `${Math.max(rect.right - 40, 8)}px`;
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const dialog = getActivePickerDialog();
    if (!dialog) return;

    const target = event.target as Node;
    if (dialog.contains(target)) return;
    if (button?.contains(target)) return;

    handleClose(event);
  };

  const ensureControls = (): boolean => {
    const dialog = getActivePickerDialog();
    const backdrop = getActivePickerBackdrop();
    if (!dialog || !backdrop) return false;

    if (!button) {
      button = document.createElement("button");
      button.type = "button";
      button.className = "google-picker-close-btn";
      button.setAttribute("aria-label", "Close Google Drive picker");
      button.title = "Close";
      button.textContent = "×";
      button.addEventListener("click", handleClose);
      document.body.appendChild(button);
    }

    if (!outsideClickAttached) {
      document.addEventListener("click", handleOutsideClick, true);
      outsideClickAttached = true;
    }

    layoutPickerDialog();
    repositionButton();
    return true;
  };

  const tick = () => {
    attempts += 1;
    ensureControls();
    if (attempts < 120 && getActivePickerDialog()) {
      rafId = window.requestAnimationFrame(tick);
    }
  };

  observer = new MutationObserver(() => {
    ensureControls();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const onLayoutChange = () => {
    layoutPickerDialog();
    ensureControls();
  };
  window.addEventListener("resize", onLayoutChange);
  window.addEventListener("scroll", onLayoutChange, true);

  if (!ensureControls()) {
    rafId = window.requestAnimationFrame(tick);
  }

  pickerControlsCleanup = () => {
    observer?.disconnect();
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
    window.removeEventListener("resize", onLayoutChange);
    window.removeEventListener("scroll", onLayoutChange, true);
    if (outsideClickAttached) {
      document.removeEventListener("click", handleOutsideClick, true);
    }
    button?.remove();
    button = null;
  };
}

function getPickerDimensions(): { width: number; height: number } {
  const width = Math.min(1051, Math.max(360, window.innerWidth - 32));
  const height = Math.min(680, Math.max(420, window.innerHeight - 32));
  return { width, height };
}

function layoutPickerDialog(): void {
  const dialog = getActivePickerDialog();
  const backdrop = getActivePickerBackdrop();
  if (!dialog) return;

  const { width, height } = getPickerDimensions();
  const inset = 8;

  if (backdrop) {
    backdrop.style.position = "fixed";
    backdrop.style.inset = "0";
    backdrop.style.width = "100%";
    backdrop.style.height = "100%";
  }

  dialog.style.position = "fixed";
  dialog.style.top = "50%";
  dialog.style.left = "50%";
  dialog.style.right = "auto";
  dialog.style.bottom = "auto";
  dialog.style.transform = "translate(-50%, -50%)";
  dialog.style.margin = "0";
  dialog.style.width = `${width}px`;
  dialog.style.height = `${height}px`;

  const content = dialog.querySelector<HTMLElement>(".picker-dialog-content");
  if (content) {
    content.style.width = `${width - inset}px`;
    content.style.height = `${height - inset}px`;
  }

  const iframe = dialog.querySelector<HTMLIFrameElement>("iframe");
  if (iframe) {
    iframe.style.width = `${width - inset}px`;
    iframe.style.height = `${height - inset}px`;
  }
}

function dismissActivePicker(): void {
  removePickerControls();

  // Defer teardown so the closing click does not retarget to UI underneath.
  window.requestAnimationFrame(() => {
    if (activePickerInstance) {
      try {
        activePickerInstance.setVisible(false);
      } catch {
        // Picker may already be torn down by Google.
      }
      activePickerInstance = null;
    }
    dismissStalePickerLayers();
  });
}

/** Request third-party cookie access while the user gesture is still active. */
function requestGooglePickerStorageAccess(): Promise<void> {
  if (typeof document.requestStorageAccess !== "function") {
    return Promise.resolve();
  }

  const hasStorageAccess = document.hasStorageAccess?.bind(document);
  if (hasStorageAccess) {
    return hasStorageAccess()
      .then((granted) => (granted ? undefined : document.requestStorageAccess()))
      .catch(() => undefined);
  }

  return document.requestStorageAccess().catch(() => undefined);
}

function dismissStalePickerLayers(): void {
  document.querySelectorAll(".picker-dialog-bg, .picker-dialog").forEach((node) => {
    node.remove();
  });
}

function loadPickerScript(): Promise<void> {
  if (pickerScriptPromise) {
    return pickerScriptPromise;
  }

  pickerScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.picker) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-picker="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Picker")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.defer = true;
    script.dataset.googlePicker = "true";
    script.onload = () => {
      if (!window.gapi) {
        reject(new Error("Failed to load Google Picker"));
        return;
      }
      window.gapi.load("picker", () => resolve());
    };
    script.onerror = () => reject(new Error("Failed to load Google Picker"));
    document.head.appendChild(script);
  });

  return pickerScriptPromise;
}

function mapPickerDocuments(
  docs: GooglePickerDocument[],
  accessToken: string
): PickerSelectedFile[] {
  return docs.map((doc) => ({
    id: doc.id,
    name: doc.name,
    mimeType: doc.mimeType,
    size: doc.sizeBytes ?? null,
    resourceKey: doc.resourceKey || null,
    accessToken,
  }));
}

export interface OpenGooglePickerOptions {
  multiple?: boolean;
  mimeTypes?: readonly string[];
}

export function getGoogleDriveReturnPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export const GOOGLE_DRIVE_IFRAME_COOKIE_HELP =
  "Google Drive picker needs cookies from accounts.google.com. Click Allow cookies in the Google dialog, or enable third-party cookies for this site in your browser settings.";

export const GOOGLE_DRIVE_NEW_TAB_HELP =
  "Opens the modern Google Drive picker in a new browser tab (recommended). Most browsers block third-party cookies by default, which breaks the embedded picker inside the app.";

export const GOOGLE_DRIVE_INLINE_PICKER_HELP =
  "Opens an older embedded picker inside the app. Use only if pop-ups are blocked or a new tab is not an option. You may need to allow third-party cookies for accounts.google.com.";

/**
 * Maps Google OAuth / OnePick error codes from redirect query params.
 * Returns null when the user cancelled — no error banner should be shown.
 */
export function formatOnePickOAuthError(code: string): string | null {
  const normalized = code.trim().toLowerCase();
  if (normalized === "access_denied") return null;

  if (code.includes(" ")) {
    return code;
  }

  switch (normalized) {
    case "invalid_scope":
      return "Google Drive access was not granted. Reconnect Google Drive and try again.";
    case "server_error":
    case "temporarily_unavailable":
      return "Google sign-in is temporarily unavailable. Try again in a few minutes.";
    default:
      return "Google Drive could not be opened. Try again or reconnect Google Drive.";
  }
}

/** True when third-party cookies for Google are likely available for the iframe picker. */
export async function canUseIframePicker(): Promise<boolean> {
  if (typeof document.hasStorageAccess !== "function") {
    return true;
  }

  try {
    await requestGooglePickerStorageAccess();
    return await document.hasStorageAccess();
  } catch {
    return false;
  }
}

export async function openGoogleDriveOnePick(
  options: OpenGooglePickerOptions & { returnPath?: string }
): Promise<void> {
  const { url } = await startGoogleDriveOnePick({
    returnPath: options.returnPath ?? getGoogleDriveReturnPath(),
    mimeTypes: options.mimeTypes ? [...options.mimeTypes] : undefined,
    multiple: options.multiple,
  });

  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    throw new Error(
      "Could not open Google Drive in a new tab. Allow pop-ups for this site and try again."
    );
  }
}

export async function openGooglePicker(
  options: OpenGooglePickerOptions = {}
): Promise<PickerSelectedFile[]> {
  if (activePickerSession) {
    return activePickerSession;
  }

  activePickerSession = openGooglePickerSession(options).finally(() => {
    activePickerSession = null;
  });

  return activePickerSession;
}

async function openGooglePickerSession(
  options: OpenGooglePickerOptions
): Promise<PickerSelectedFile[]> {
  const storageAccessPromise = requestGooglePickerStorageAccess();
  const [config] = await Promise.all([getGoogleDrivePickerConfig(), storageAccessPromise]);
  await loadPickerScript();

  const picker = window.google?.picker;
  if (!picker) {
    throw new Error("Google Picker failed to initialize");
  }

  dismissStalePickerLayers();

  const mimeTypes = (options.mimeTypes ?? CONTRACT_PICKER_MIME_TYPES).join(",");
  const origin = `${window.location.protocol}//${window.location.host}`;

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (
      handler: (value: PickerSelectedFile[] | PromiseLike<PickerSelectedFile[]>) => void,
      value: PickerSelectedFile[]
    ) => {
      if (settled) return;
      settled = true;
      dismissActivePicker();
      handler(value);
    };

    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setMimeTypes(mimeTypes)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);

    const { width, height } = getPickerDimensions();

    const builder = new picker.PickerBuilder()
      .setOAuthToken(config.accessToken)
      .setDeveloperKey(config.developerKey)
      .setAppId(config.appId)
      .setOrigin(origin)
      .setSize(width, height)
      .addView(view)
      .enableFeature(picker.Feature.SUPPORT_DRIVES)
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) {
          const docs = data.docs ?? [];
          const failedDoc = docs.find((doc) => doc.driveError);
          if (failedDoc?.driveError) {
            if (!settled) {
              settled = true;
              dismissActivePicker();
              reject(
                new Error(`Google Drive could not open "${failedDoc.name}": ${failedDoc.driveError}`)
              );
            }
            return;
          }

          finish(resolve, mapPickerDocuments(docs, config.accessToken));
          return;
        }

        if (data.action === picker.Action.CANCEL) {
          finish(resolve, []);
        }
      });

    if (options.multiple) {
      builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
    }

    const pickerWidget = builder.build();
    activePickerInstance = pickerWidget;
    pickerWidget.setVisible(true);
    attachPickerControls(() => {
      finish(resolve, []);
    });
  });
}

export function isSelectableContractFile(mimeType: string): boolean {
  return (
    mimeType === "application/pdf" ||
    mimeType === DOCX_MIME ||
    mimeType === GOOGLE_DOC_MIME
  );
}

export function filterPickerFiles(
  files: PickerSelectedFile[],
  predicate: (mimeType: string) => boolean
): PickerSelectedFile[] {
  return files.filter((file) => predicate(file.mimeType));
}
